/**
 * CourseAssignmentsTab — flat aggregate view of ALL assignments across all units.
 * Groups by unit. Read-only view with add/edit/delete per assignment.
 */
import { useState } from 'react';
import { ClipboardList, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { CourseAssignmentForm, type CourseAssignmentFormValues } from '@client/components/forms/course-assignment-form';
import {
  useCourseUnits,
  useCourseAssignments,
  useCreateCourseAssignment,
  useUpdateCourseAssignment,
  useDeleteCourseAssignment,
  type CourseAssignment,
  type CourseUnit,
} from '@client/lib/queries/use-curriculum';

const ASSIGNMENT_TYPE_LABELS: Record<string, string> = {
  homework: 'Bài tập',
  quiz: 'Kiểm tra',
  project: 'Dự án',
};

const ASSIGNMENT_TYPE_COLORS: Record<string, string> = {
  homework: 'bg-brand-green-100 text-brand-green-700',
  quiz: 'bg-brand-orange-100 text-brand-orange-700',
  project: 'bg-fun-violet-100 text-fun-violet-700',
};

// ---------------------------------------------------------------------------
// Per-unit assignment group
// ---------------------------------------------------------------------------
interface UnitAssignmentGroupProps {
  unit: CourseUnit;
  onEdit: (a: CourseAssignment, unitId: string) => void;
  onDelete: (a: CourseAssignment) => void;
  onAdd: (unitId: string) => void;
}

function UnitAssignmentGroup({ unit, onEdit, onDelete, onAdd }: UnitAssignmentGroupProps) {
  const { data: assignments, isLoading } = useCourseAssignments({ unitId: unit.id });
  const sorted = [...(assignments ?? [])].sort((a, b) =>
    a.title.localeCompare(b.title),
  );

  return (
    <Card className="shadow-none">
      <CardHeader className="px-4 py-3 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm font-semibold">{unit.name}</CardTitle>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs gap-1"
          onClick={() => onAdd(unit.id)}
        >
          <Plus className="h-3 w-3" /> Thêm
        </Button>
      </CardHeader>

      <CardContent className="px-4 pb-4 pt-0">
        {isLoading ? (
          <div className="flex flex-col gap-2">
            {[1, 2].map((i) => <Skeleton key={i} className="h-8 w-full" />)}
          </div>
        ) : sorted.length === 0 ? (
          <p className="text-xs text-muted-foreground">Chưa có bài tập nào trong chương này.</p>
        ) : (
          <div className="flex flex-col gap-1.5">
            {sorted.map((a) => (
              <div
                key={a.id}
                className="flex items-center gap-2 px-3 py-2 rounded border border-border bg-card text-sm group"
              >
                <Badge
                  className={`${ASSIGNMENT_TYPE_COLORS[a.type] ?? ''} border-0 text-xs px-1.5 py-0 shrink-0`}
                >
                  {ASSIGNMENT_TYPE_LABELS[a.type] ?? a.type}
                </Badge>

                <span className="flex-1 min-w-0 truncate font-medium">{a.title}</span>

                {a.maxScore != null && (
                  <span className="text-xs text-muted-foreground shrink-0">{a.maxScore}đ</span>
                )}
                {a.dueDaysAfterLesson != null && (
                  <span className="text-xs text-muted-foreground shrink-0">
                    {a.dueDaysAfterLesson} ngày
                  </span>
                )}

                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => onEdit(a, unit.id)}
                    aria-label="Sửa bài tập"
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 hover:text-destructive"
                    onClick={() => onDelete(a)}
                    aria-label="Xóa bài tập"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------
interface CourseAssignmentsTabProps {
  courseId: string;
}

export function CourseAssignmentsTab({ courseId }: CourseAssignmentsTabProps) {
  const { data: units, isLoading: unitsLoading, isError } = useCourseUnits(courseId);

  const createAssignment = useCreateCourseAssignment();
  const deleteAssignment = useDeleteCourseAssignment();

  const [addUnitId, setAddUnitId] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<{ assignment: CourseAssignment; unitId: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CourseAssignment | null>(null);

  const updateAssignment = useUpdateCourseAssignment(editTarget?.assignment.id ?? '');

  const sorted = [...(units ?? [])].sort((a, b) => a.orderNo - b.orderNo);

  async function handleCreate(values: CourseAssignmentFormValues) {
    if (!addUnitId) return;
    try {
      await createAssignment.mutateAsync({
        unitId: addUnitId,
        type: values.type,
        title: values.title,
        description: values.description || undefined,
        maxScore: values.maxScore !== '' && values.maxScore !== undefined ? Number(values.maxScore) : undefined,
        dueDaysAfterLesson:
          values.dueDaysAfterLesson !== '' && values.dueDaysAfterLesson !== undefined
            ? Number(values.dueDaysAfterLesson)
            : undefined,
      });
      toast.success('Đã thêm bài tập.');
      setAddUnitId(null);
    } catch {
      toast.error('Không thêm được bài tập.');
    }
  }

  async function handleUpdate(values: CourseAssignmentFormValues) {
    if (!editTarget) return;
    try {
      await updateAssignment.mutateAsync({
        type: values.type,
        title: values.title,
        description: values.description || undefined,
        maxScore: values.maxScore !== '' && values.maxScore !== undefined ? Number(values.maxScore) : undefined,
        dueDaysAfterLesson:
          values.dueDaysAfterLesson !== '' && values.dueDaysAfterLesson !== undefined
            ? Number(values.dueDaysAfterLesson)
            : undefined,
      });
      toast.success('Đã cập nhật bài tập.');
      setEditTarget(null);
    } catch {
      toast.error('Không cập nhật được bài tập.');
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await deleteAssignment.mutateAsync(deleteTarget.id);
      toast.success('Đã xóa bài tập.');
    } catch {
      toast.error('Không xóa được bài tập.');
    } finally {
      setDeleteTarget(null);
    }
  }

  if (unitsLoading) {
    return (
      <div className="flex flex-col gap-3">
        {[1, 2].map((i) => <Skeleton key={i} className="h-32 w-full rounded-lg" />)}
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Không tải được dữ liệu.</AlertDescription>
      </Alert>
    );
  }

  if (sorted.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <ClipboardList className="h-10 w-10 mx-auto mb-3 opacity-30" />
        <p className="text-sm">Chưa có chương nào. Thêm chương trong tab "Units &amp; Lessons" trước.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-base font-semibold mb-1">Tất cả bài tập theo chương</h3>

      {sorted.map((unit) => (
        <UnitAssignmentGroup
          key={unit.id}
          unit={unit}
          onAdd={(uid) => setAddUnitId(uid)}
          onEdit={(a, uid) => setEditTarget({ assignment: a, unitId: uid })}
          onDelete={(a) => setDeleteTarget(a)}
        />
      ))}

      {/* Add dialog */}
      <Dialog open={!!addUnitId} onOpenChange={(v) => { if (!v) setAddUnitId(null); }}>
        <DialogContent className="sm:max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Thêm bài tập</DialogTitle>
          </DialogHeader>
          <CourseAssignmentForm
            isSubmitting={createAssignment.isPending}
            onSubmit={handleCreate}
            onCancel={() => setAddUnitId(null)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={(v) => { if (!v) setEditTarget(null); }}>
        <DialogContent className="sm:max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Sửa bài tập</DialogTitle></DialogHeader>
          {editTarget && (
            <CourseAssignmentForm
              defaultValues={{
                type: editTarget.assignment.type,
                title: editTarget.assignment.title,
                description: editTarget.assignment.description ?? '',
                maxScore: editTarget.assignment.maxScore ?? '',
                dueDaysAfterLesson: editTarget.assignment.dueDaysAfterLesson ?? '',
              }}
              isSubmitting={updateAssignment.isPending}
              onSubmit={handleUpdate}
              onCancel={() => setEditTarget(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <DialogContent className="sm:max-w-sm w-[calc(100vw-2rem)]">
          <DialogHeader><DialogTitle>Xóa bài tập?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Xóa <strong>{deleteTarget?.title}</strong>? Không thể hoàn tác.
          </p>
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end mt-2">
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Hủy</Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteAssignment.isPending}
            >
              {deleteAssignment.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Xóa'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
